import { User } from "oidc-client-ts";
import { QueryObserverOptions } from "@tanstack/react-query";
import { doGet, doPost } from "./util/apiHelper.ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { CHAT_QUERY_KEYS } from "./util/queryKeys.ts";

export interface ChatListResponse {
  chats: Chat[];
}

export interface MessagesResponse {
  messages: Message[];
}

export interface Chat {
  chatId: string;
  imageUrl?: string;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
  participant: Participant;
}

export interface Participant {
  name: string;
  id: string;
}

export interface MessageMetadata {
  timestamp: string;
}

export interface Attachment {
  type: "image" | "file" | "video";
  url: string;
  filename: string;
  size?: number;
}

export interface MessageContent {
  text: string;
  metadata?: MessageMetadata;
}

export interface Message {
  id: string;
  chatId: string;
  userId: string;
  userName: string;
  content: MessageContent;
  createdAt: string;
  modifiedAt?: string;
}

export interface MessageRequest {
  chatId: string;
  text: string;
}

async function getChats(user: User): Promise<Chat[]> {
  return (
    await doGet<ChatListResponse>(`/users/${user.profile.sub}/chats`, user)
  ).chats;
}

async function getMessages(user: User, chatId: string): Promise<Message[]> {
  return (
    await doGet<MessagesResponse>(
      `/users/${user.profile.sub}/chats/${chatId}/messages`,
      user,
    )
  ).messages;
}

async function postMessage(
  user: User,
  messageRequest: MessageRequest,
): Promise<Message> {
  return await doPost<Message>(
    `/users/${user.profile.sub}/chats/${messageRequest.chatId}/messages`,
    user,
    messageRequest,
  );
}

async function postChat(user: User, participantId: string): Promise<Chat> {
  return await doPost<Chat>(
    `/users/${user.profile.sub}/chats`,
    user,
    undefined,
    {
      participantId: participantId,
    },
  );
}

export function useChatsQuery(
  options?: Omit<QueryObserverOptions<Chat[]>, "queryKey" | "queryFn">,
) {
  return useAuthenticatedQuery(
    CHAT_QUERY_KEYS.chats,
    async (user) => await getChats(user),
    options,
  );
}

export function useChatMutation() {
  return useAuthenticatedMutation(
    async (participantId: string, user) => await postChat(user, participantId),
    { invalidateKeys: [CHAT_QUERY_KEYS.chats] },
  );
}

export function useMessagesQuery(
  chatId?: string,
  options?: Omit<QueryObserverOptions<Message[]>, "queryKey" | "queryFn">,
) {
  return useAuthenticatedQuery(
    [...CHAT_QUERY_KEYS.messages, chatId],
    async (user) => {
      if (chatId == null) return [];
      return getMessages(user, chatId);
    },
    {
      initialData: [],
      ...options,
    },
  );
}

export function useMessageMutation() {
  return useAuthenticatedMutation(
    async (messageRequest: MessageRequest, user) =>
      await postMessage(user, messageRequest),
    { invalidateKeys: [CHAT_QUERY_KEYS.messages, CHAT_QUERY_KEYS.chats] },
  );
}
